/**
 * Canada Post civic addressing: unit may appear before the civic number with a hyphen
 * (e.g. "713-5950 Boulevard Étienne-Dallaire" → unit 713, civic 5950).
 * Close numeric ranges like "5950-5952" stay as a single civic number/range.
 */

export type CanadianUnitCivicParts = {
  /** Apartment / unit when using unit-civic hyphen form. */
  unit?: string
  /** Municipal civic number (or range like 5950-5952). */
  civicNumber?: string
  /** Street line for the form / map: civic number + road name (no unit). */
  streetLine: string
  /** Query string safe to send to geocoders (unit stripped). */
  geocodeQuery: string
}

function isLikelyCivicNumberRange(left: number, right: number): boolean {
  const diff = Math.abs(left - right)
  // Consecutive / near house ranges (5950-5952), not apt-civic (713-5950).
  return diff > 0 && diff <= 20 && Math.min(left, right) >= 10
}

/**
 * Split a leading Canada Post "unit-civic" prefix from an address line.
 * Leaves civic ranges (5950-5952 …) intact.
 */
export function splitCanadianUnitCivicAddress(raw: string): CanadianUnitCivicParts {
  const trimmed = raw.trim().replace(/\s+/g, ' ')
  if (!trimmed) {
    return { streetLine: '', geocodeQuery: '' }
  }

  const match = trimmed.match(/^(\d+)\s*[-–]\s*(\d+[A-Za-z]?)\b(?:\s+(.*))?$/)
  if (!match) {
    return { streetLine: trimmed, geocodeQuery: trimmed }
  }

  const leftRaw = match[1]
  const rightRaw = match[2]
  const remainder = (match[3] || '').trim()
  const left = Number.parseInt(leftRaw, 10)
  const right = Number.parseInt(rightRaw, 10)

  if (
    Number.isFinite(left) &&
    Number.isFinite(right) &&
    isLikelyCivicNumberRange(left, right)
  ) {
    const civicNumber = `${leftRaw}-${rightRaw}`
    const streetLine = remainder ? `${civicNumber} ${remainder}` : civicNumber
    return { civicNumber, streetLine, geocodeQuery: streetLine }
  }

  const civicNumber = rightRaw
  const streetLine = remainder ? `${civicNumber} ${remainder}` : civicNumber
  return {
    unit: leftRaw,
    civicNumber,
    streetLine,
    geocodeQuery: streetLine,
  }
}

/** Apply unit-civic split to a parsed street line (and optional known house number). */
export function applyUnitCivicToStreetParts(parts: {
  street: string
  number?: string
  unit?: string
}): { street: string; number?: string; unit?: string } {
  const split = splitCanadianUnitCivicAddress(parts.street)
  if (!split.unit) {
    return parts
  }

  return {
    street: split.streetLine,
    number: split.civicNumber || parts.number,
    unit: parts.unit || split.unit,
  }
}
